import { useState } from "react";
import {
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  Plus,
  Search,
  Settings,
  RotateCw,
  FilterX,
  X,
  Pencil,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const sortOptions = [
  { value: "newest", label: "Newest First" },
  { value: "oldest", label: "Oldest First" },
  { value: "nameAZ", label: "Name (A-Z)" },
  { value: "nameZA", label: "Name (Z-A)" },
];

// Sample data for demonstration
const karigars = [
  {
    id: "KR-1",
    name: "John Doe",
    contact: "+91 9876543210",
    specialization: "Stitching",
    activeJobs: 3,
    joinDate: "15",
    joinMonth: "MAY",
    status: "Active",
  },
  {
    id: "KR-2",
    name: "Jane Smith",
    contact: "+91 9876543211",
    specialization: "Embroidery",
    activeJobs: 1,
    joinDate: "14",
    joinMonth: "MAY",
    status: "Inactive",
  },
];

const Karigar = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  // Define columns
  const defaultColumns = [
    { id: "date", label: "Join Date", required: true },
    { id: "karigarId", label: "Karigar ID", required: true },
    { id: "name", label: "Name", required: true },
    { id: "contact", label: "Contact", required: true },
    { id: "specialization", label: "Specialization", required: true },
    { id: "activeJobs", label: "Active Jobs", required: false },
    { id: "status", label: "Status", required: true },
  ];

  const [visibleColumns, setVisibleColumns] = useState(
    defaultColumns.reduce((acc, col) => ({ ...acc, [col.id]: true }), {}),
  );

  const [filters, setFilters] = useState({
    dateRange: "",
    sortBy: "",
  });

  const handleSearchChange = event => {
    setSearchQuery(event.target.value);
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  const handleSettingsClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseSettings = () => {
    setAnchorEl(null);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    // Simulate refresh
    setTimeout(() => {
      setIsRefreshing(false);
    }, 1000);
  };

  const clearAllFilters = () => {
    setSearchQuery("");
    setFilters({
      dateRange: "",
      sortBy: "",
    });
  };

  return (
    <div className="bg-white h-full">
      {/* Header Section */}
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-semibold font-outfit text-gray-800">
            Karigar Management
          </h1>
          <button
            className="px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium hover:bg-[#4A47A3] flex items-center gap-2"
            onClick={() => navigate("/production/karigar/create")}>
            <Plus size={16} />
            Add Karigar
          </button>
        </div>

        {/* Search Bar */}
        <div className="flex items-center gap-4">
          <div className="w-[450px]">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2"
                size={18}
                color="#83899F"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search by Karigar ID, Name, Contact..."
                className="w-full h-10 pl-10 pr-10 rounded-lg border border-gray-200 
                         focus:border-[#5652B0] focus:ring-1 focus:ring-[#5652B0] 
                         outline-none transition-all text-gray-600"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 hover:bg-gray-100 p-1 rounded-full">
                  <X size={16} color="#83899F" />
                </button>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <IconButton
              onClick={handleRefresh}
              className="hover:bg-gray-100"
              disabled={isRefreshing}
              title="Refresh">
              <RotateCw
                size={20}
                className={`text-gray-600 ${
                  isRefreshing ? "animate-spin" : ""
                }`}
              />
            </IconButton>
            <IconButton
              onClick={handleSettingsClick}
              className="hover:bg-gray-100"
              title="Column Settings">
              <Settings size={20} className="text-gray-600" />
            </IconButton>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            {/* Date Range Filter */}
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Date Range</InputLabel>
              <Select
                value={filters.dateRange}
                label="Date Range"
                onChange={e =>
                  setFilters({ ...filters, dateRange: e.target.value })
                }>
                <MenuItem value="">All Time</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="yesterday">Yesterday</MenuItem>
                <MenuItem value="last7days">Last 7 Days</MenuItem>
                <MenuItem value="last30days">Last 30 Days</MenuItem>
                <MenuItem value="thisMonth">This Month</MenuItem>
              </Select>
            </FormControl>

            {/* Sort By Filter */}
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={filters.sortBy}
                label="Sort By"
                onChange={e =>
                  setFilters({ ...filters, sortBy: e.target.value })
                }>
                {sortOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          {/* Clear Filters Button */}
          {(searchQuery ||
            Object.values(filters).some(value => value !== "")) && (
            <button
              onClick={clearAllFilters}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 bg-gray-50 
                       hover:bg-gray-100 rounded-lg transition-colors">
              <FilterX size={16} />
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Table Section */}
      <div className="px-6">
        <Paper
          sx={{
            width: "100%",
            overflow: "hidden",
            boxShadow: "none",
            border: "1px solid #E5E7EB",
            borderRadius: "0.5rem",
          }}>
          <TableContainer
            sx={{ maxHeight: "calc(100vh - 300px)" }}
            className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-50">
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {defaultColumns
                    .filter(column => visibleColumns[column.id])
                    .map(column => (
                      <TableCell
                        key={column.id}
                        align="center"
                        sx={{
                          backgroundColor: "#F9FAFB",
                          fontWeight: "600",
                          fontSize: "14px",
                          color: "#374151",
                          borderBottom: "2px solid #E5E7EB",
                        }}
                        className="font-outfit">
                        {column.label}
                      </TableCell>
                    ))}
                  <TableCell
                    align="center"
                    sx={{
                      backgroundColor: "#F9FAFB",
                      fontWeight: "600",
                      fontSize: "14px",
                      color: "#374151",
                      borderBottom: "2px solid #E5E7EB",
                    }}
                    className="font-outfit">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {karigars.map(karigar => (
                  <TableRow
                    hover
                    key={karigar.id}
                    sx={{
                      "&:hover": {
                        backgroundColor: "#F9FAFB",
                      },
                    }}>
                    {visibleColumns.date && (
                      <TableCell
                        align="center"
                        sx={{
                          padding: "12px 16px",
                          fontSize: "14px",
                          color: "#4B5563",
                        }}
                        className="font-outfit">
                        {karigar.joinDate} {karigar.joinMonth}
                      </TableCell>
                    )}
                    {visibleColumns.karigarId && (
                      <TableCell
                        align="center"
                        sx={{
                          padding: "12px 16px",
                          fontSize: "14px",
                          color: "#4B5563",
                          fontWeight: 500,
                        }}
                        className="font-outfit">
                        {karigar.id}
                      </TableCell>
                    )}
                    {visibleColumns.name && (
                      <TableCell
                        align="center"
                        sx={{
                          padding: "12px 16px",
                          fontSize: "14px",
                          color: "#4B5563",
                        }}
                        className="font-outfit">
                        {karigar.name}
                      </TableCell>
                    )}
                    {visibleColumns.contact && (
                      <TableCell
                        align="center"
                        sx={{
                          padding: "12px 16px",
                          fontSize: "14px",
                          color: "#4B5563",
                        }}
                        className="font-outfit">
                        {karigar.contact}
                      </TableCell>
                    )}
                    {visibleColumns.specialization && (
                      <TableCell
                        align="center"
                        sx={{
                          padding: "12px 16px",
                          fontSize: "14px",
                          color: "#4B5563",
                        }}
                        className="font-outfit">
                        {karigar.specialization}
                      </TableCell>
                    )}
                    {visibleColumns.activeJobs && (
                      <TableCell
                        align="center"
                        sx={{
                          padding: "12px 16px",
                          fontSize: "14px",
                          color: "#4B5563",
                        }}
                        className="font-outfit">
                        {karigar.activeJobs}
                      </TableCell>
                    )}
                    {visibleColumns.status && (
                      <TableCell
                        align="center"
                        sx={{
                          padding: "12px 16px",
                          fontSize: "14px",
                          color: "#4B5563",
                        }}
                        className="font-outfit">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            karigar.status === "Active"
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}>
                          {karigar.status}
                        </span>
                      </TableCell>
                    )}
                    <TableCell
                      align="center"
                      sx={{
                        padding: "12px 16px",
                        fontSize: "14px",
                      }}
                      className="font-outfit">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          className="p-1.5 rounded-md bg-purple-50 hover:bg-purple-100 transition-colors"
                          onClick={e => {
                            e.stopPropagation();
                            navigate(
                              `/production/karigar/edit?id=${karigar.id}`,
                            );
                          }}
                          title="Edit">
                          <Pencil size={16} className="text-[#5652B0]" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {karigars.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={
                        Object.values(visibleColumns).filter(Boolean).length + 1
                      }
                      align="center"
                      sx={{
                        padding: "16px",
                        fontSize: "14px",
                        color: "#6B7280",
                      }}
                      className="font-outfit">
                      No karigars found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </div>
    </div>
  );
};

export default Karigar;
